import { AppointmentImageType, CityType, ShowroomType, EventType } from '../types/events';
import { CardEvent } from '../components/EventCard/EventCard';

// Import appointment card images
import boardroomMeetingImage from '../assets/backgroundImages/appointmentCardImages/boardroom-meeting.png';
import builderPackageImage from '../assets/backgroundImages/appointmentCardImages/builder-package-meeting.png';
import privateIntroClassImage from '../assets/backgroundImages/appointmentCardImages/private-introduction-class.png';
import privateOwnershipExperienceImage from '../assets/backgroundImages/appointmentCardImages/private-ownership-experience.png';
import productConsultationImage from '../assets/backgroundImages/appointmentCardImages/product-consultation.png';

export interface AppointmentLocation {
  territoryId: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface Appointment {
  appointmentId: string;
  name: string;
  description: string | null;
  duration: number;
  durationUnit: string;
  city: CityType;
  showroom: ShowroomType;
  location: AppointmentLocation;
  appointmentType: EventType | null;
}

export const getAppointmentType = (appointmentName: string): string => {
  const name = appointmentName.toLowerCase();
  
  // More flexible matching using includes() for variations in naming
  if (name.includes('product') && name.includes('consultation')) {
    return 'product-consultation';
  }
  
  if (name.includes('private') && (name.includes('intro') || name.includes('introduction'))) {
    return 'private-intro-class';
  }
  
  if (name.includes('private') && name.includes('ownership')) {
    return 'private-ownership';
  }
  
  if (name.includes('boardroom') || (name.includes('board') && name.includes('meeting'))) {
    return 'boardroom-meeting';
  }
  
  if (name.includes('builder') || name.includes('package')) {
    return 'builder-package-viewing';
  }
  
  // Return 'unknown-appointment' instead of defaulting to a known type
  return 'unknown-appointment';
};

const getAppointmentImageType = (appointment: Appointment): AppointmentImageType => {
  const type = appointment.name.toLowerCase();
  
  if (type.includes('boardroom') || type.includes('meeting')) return 'boardroom-meeting';
  if (type.includes('builder') || type.includes('package')) return 'builder-package-viewing-appointment';
  if (type.includes('private') && type.includes('introduction')) return 'private-introduction-class';
  if (type.includes('ownership')) return 'private-ownership-experience';
  return 'product-consultation';
};

const getAppointmentImage = (imageType: AppointmentImageType): string => {
  switch (imageType) {
    case 'boardroom-meeting':
      return boardroomMeetingImage;
    case 'builder-package-viewing-appointment':
      return builderPackageImage;
    case 'private-introduction-class':
      return privateIntroClassImage;
    case 'private-ownership-experience':
      return privateOwnershipExperienceImage;
    default:
      return productConsultationImage;
  }
};


export const adaptAppointmentToCardEvent = (appointment: Appointment): CardEvent => {
  const imageEventType = getAppointmentImageType(appointment);
  const formType = getAppointmentType(appointment.name);

  return {
    id: appointment.appointmentId,
    remoteObjectId: appointment.appointmentId,
    title: appointment.name.toUpperCase(),
    // Use appointmentType from API with fallback to In-Person
    type: appointment.appointmentType || 'In-Person',
    duration: `${appointment.duration}${appointment.durationUnit.toLowerCase() === 'minutes' ? 'min' : 'hr'}`,
    description: appointment.description || '',
    date: 'Available', // We'll need to get actual dates from a separate API call
    time: 'Multiple Times',
    location: appointment.location.address,
    city: appointment.city,
    showroom: appointment.showroom,
    imageUrl: getAppointmentImage(imageEventType),
    imageEventType,
    formType
  };
};
